import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  MapScale,
  useOpenLayersContext,
  useLayers,
} from '@ais-ui/react-openlayers';
import { useTimeZoneContext } from '@ais-ui/utils';
import {
  PiMinus,
  PiMonitorPlay,
  PiMouse,
  PiMouseLeftClickFill,
  PiPlus,
} from 'react-icons/pi';
import { useCursorPosition } from '../contexts/CursorPositionContext';
import { useMapOverlay } from '../contexts/MapOverlayContext';
import { useRemoteZoomPulse } from '../contexts/RemoteZoomPulseContext';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { useSettings } from '../../Settings/SettingsProvider';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { useLengthUnits } from '../../Units/useLengthUnits';
import { useReplayContext } from '../../Replay/ReplayContext';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';
import { ROUTES } from '../../../routes';

interface MetadataBadgeTooltipProps {
  description: string;
  settingsLabel?: string;
  children: React.ReactNode;
}

const MetadataBadgeTooltip = ({
  description,
  settingsLabel,
  children,
}: MetadataBadgeTooltipProps) => (
  <Tooltip>
    <TooltipTrigger className="w-fit">{children}</TooltipTrigger>
    <TooltipContent side="left">
      <p>{description}</p>
      {settingsLabel && (
        <Link
          to={ROUTES.SETTINGS.GENERAL.$path()}
          className="text-primary underline"
        >
          {settingsLabel}
        </Link>
      )}
    </TooltipContent>
  </Tooltip>
);

export const CursorPositionBadge = () => {
  const { cursorPosition } = useCursorPosition();
  const { settings } = useSettings();

  if (!settings['ribbonPlugin.settings']?.show_cursor_position) {
    return null;
  }

  if (!cursorPosition) {
    return null;
  }

  return (
    <MetadataBadgeTooltip
      description="Current cursor position on the map."
      settingsLabel="Change format in Settings"
    >
      <Badge
        variant="secondary"
        className="bg-card dark:bg-card/90 shadow-sm text-sm w-fit cursor-default hover:bg-card text-card-foreground"
      >
        {cursorPosition.lat} {cursorPosition.lon}
      </Badge>
    </MetadataBadgeTooltip>
  );
};

export const TargetCountBadge = () => {
  const { targetCountInfo } = useMapOverlay();
  const { settings } = useSettings();
  const permissions = usePermissions();

  if (!settings.showTargetCountBadge) {
    return null;
  }

  if (!permissions.admin) {
    return null;
  }

  if (!targetCountInfo) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card dark:bg-card/90 shadow-sm text-sm w-fit cursor-default hover:bg-card text-card-foreground"
    >
      {targetCountInfo}
    </Badge>
  );
};

const ATTRIBUTIONS = [
  {
    layerType: 'osm',
    label: 'OpenStreetMap',
    href: 'https://www.openstreetmap.org/copyright',
  },
  {
    layerType: 'openseamap',
    label: 'OpenSeaMap',
    href: 'https://www.openseamap.org/',
  },
] as const;

export const AttributionBadge = () => {
  const { layers } = useLayers();

  const activeTypes = useMemo(() => {
    const types = new Set<string>();
    for (const layer of layers) {
      if (layer.isBase) types.add(layer.name);
      if (layer.isVisible) types.add(layer.type);
    }
    return types;
  }, [layers]);

  const visible = ATTRIBUTIONS.filter((a) => activeTypes.has(a.layerType));

  if (visible.length === 0) return null;

  return (
    <Badge
      variant="secondary"
      className="bg-card dark:bg-card/90 shadow-sm text-sm w-fit cursor-default hover:bg-card text-card-foreground"
    >
      <span>
        {visible.map((a, i) => (
          <Fragment key={a.layerType}>
            {i > 0 && ' | '}
            {'©'}
            <a
              href={a.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline cursor-pointer"
            >
              {a.label}
            </a>
          </Fragment>
        ))}
      </span>
    </Badge>
  );
};

export const RemoteControlBadge = () => {
  const { settings } = useSettings();
  const { pulseKey } = useRemoteZoomPulse();
  const [isClicked, setIsClicked] = useState(false);
  const prevPulseKey = useRef(pulseKey);

  const isRemoteControlEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.remotecontrol_enabled ?? false;
  }, [settings]);

  useEffect(() => {
    if (pulseKey === prevPulseKey.current) return;
    prevPulseKey.current = pulseKey;

    setIsClicked(true);
    const timer = setTimeout(() => setIsClicked(false), 2500);
    return () => clearTimeout(timer);
  }, [pulseKey]);

  if (!isRemoteControlEnabled) {
    return null;
  }

  return (
    <MetadataBadgeTooltip
      description="External applications can control this view."
      settingsLabel="Manage in Settings"
    >
      <Badge
        variant="secondary"
        className="bg-card dark:bg-card/90 shadow-sm text-sm cursor-default hover:bg-card space-x-2 text-card-foreground"
      >
        <span className="relative inline-flex items-center justify-center size-4">
          <PiMouse className="size-4" />
          {isClicked && (
            <PiMouseLeftClickFill
              key={pulseKey}
              className="absolute inset-0 size-4 text-primary animate-remote-click"
            />
          )}
        </span>
        <p>Remote control active</p>
      </Badge>
    </MetadataBadgeTooltip>
  );
};

export const MapPanelScale = () => {
  const { settings } = useSettings();

  const { selectedUnit } = useLengthUnits();

  const isMapScaleEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.mapscale_enabled ?? false;
  }, [settings]);

  if (!isMapScaleEnabled) {
    return null;
  }

  return <MapScale unitSystem={selectedUnit} className="shadow-sm"></MapScale>;
};

export const MapPanelZoomButtons = () => {
  const { settings } = useSettings();

  const { map } = useOpenLayersContext();

  const isZoomButtonsEnabled = useMemo(() => {
    return settings['ribbonPlugin.settings']?.zoom_buttons_enabled ?? false;
  }, [settings]);

  const handleZoomIn = () => {
    if (!map) {
      return;
    }
    const view = map.getView();
    const currentZoom = view.getZoom();

    if (!currentZoom) {
      return;
    }

    view.animate({
      zoom: currentZoom + 1,
      duration: 250,
    });
  };

  const handleZoomOut = () => {
    if (!map) {
      return;
    }
    const view = map.getView();
    const currentZoom = view.getZoom();
    if (!currentZoom) {
      return;
    }
    view.animate({
      zoom: currentZoom - 1,
      duration: 250,
    });
  };

  if (!isZoomButtonsEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col items-center bg-background border rounded-lg shadow-sm">
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        onClick={handleZoomIn}
      >
        <PiPlus></PiPlus>
      </Button>
      <Separator className="w-4/6"></Separator>
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        onClick={handleZoomOut}
      >
        <PiMinus></PiMinus>
      </Button>
    </div>
  );
};

export const CreatedAtBadge = () => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { createdAt } = useMapOverlay();

  if (!createdAt) {
    return null;
  }

  return (
    <MetadataBadgeTooltip description="The point in time the displayed vessel positions are from.">
      <Badge
        variant="secondary"
        className="bg-card dark:bg-card/90 shadow-sm text-sm w-fit cursor-default hover:bg-card text-card-foreground"
      >
        {formatDateTimeWithTimeZone(createdAt)}
      </Badge>
    </MetadataBadgeTooltip>
  );
};

export const ReplayTimeBadge = () => {
  const { currentTime } = useReplayContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { viewMode } = useViewMode();

  if (!currentTime || viewMode !== ViewMode.Replay) {
    return null;
  }

  return (
    <Badge
      variant="secondary"
      className="bg-card dark:bg-card/90 shadow-sm text-sm w-fit cursor-default hover:bg-card space-x-2 text-card-foreground"
    >
      <PiMonitorPlay></PiMonitorPlay>
      <p>{formatDateTimeWithTimeZone(currentTime)}</p>
    </Badge>
  );
};

export const TimeZoneBadge = () => {
  const { timeZone } = useTimeZoneContext();

  if (!timeZone) {
    return null;
  }

  return (
    <MetadataBadgeTooltip
      description="Currently selected timezone."
      settingsLabel="Change in Settings"
    >
      <Badge
        variant="secondary"
        className="bg-card dark:bg-card/90 shadow-sm text-sm w-fit cursor-default hover:bg-card text-card-foreground"
      >
        {timeZone}
      </Badge>
    </MetadataBadgeTooltip>
  );
};
